
import {Component, Prop, Vue} from "vue-property-decorator";
import {NOTR_ROL_RENGI, rolRengi} from "@/enum/YetkiModulleri";
import KisiOzelYetkiDialog from "@/components/forms/KisiOzelYetkiDialog.vue";
import {duzenlemeEngeli} from "@/components/izinler/duzenlemeEngeli";
import {rolDegisimiOnayGerekir, rolDegisimiOnayMetni} from "@/components/izinler/rolDegisimOnayi";
// HTTP hataları global axios kesicisinde (plugins/axios.ts) gösterilir.

@Component({
  components: {KisiOzelYetkiDialog},
})
export default class KisilerRolAtama extends Vue {
  // DEV-2113 kısıtlı dönem: rol ataması salt-okunur.
  @Prop({type: Boolean, default: false}) islemKisitli!: boolean;

  personeller: Array<any> = [];
  roller: Array<any> = [];
  // mounted hemen yükler; ilk çizimde de boş durum görünmesin.
  yukleniyor = true;
  yuklemeHatasi = false;
  yuklemeNo = 0;
  // Ataması süren kişiler: satırları kilitli, aynı kişiye ikinci istek gitmez.
  atananlar: number[] = [];
  // Ad/mail arama filtresi (yalnız görünüm; sayaç rozeti tam listeyi sayar).
  arama = "";
  // Kişiye özel yetki diyaloğu (tek örnek; açılan kişi ozelYetkiKisi'dir).
  ozelYetkiAcik = false;
  ozelYetkiKisi: any = null;
  // Rol değişimi onayı: onay bekleyen {item, rolId}. Seçici, vazgeçilince eski role dönsün diye
  // kişi başına sayaçla yeniden çizilir (v-select kendi iç değerini tutar).
  rolOnayAcik = false;
  rolOnayBekleyen: { item: any; rolId: number } | null = null;
  secimYenileme: { [kisiId: number]: number } = {};

  // Sayı yalnız liste gerçekten geldiyse; yüklenirken ya da hata varken "0 kişi" denmez.
  get baslikMetni(): string {
    if (this.personeller.length === 0 && (this.yukleniyor || this.yuklemeHatasi)) {
      return "Kişiler";
    }
    return `${this.personeller.length} kişi`;
  }

  // Arama kutusuna göre süzülmüş kişiler (boşsa tam liste).
  get filtrelenmis(): any[] {
    const q = (this.arama || "").trim().toLocaleLowerCase("tr");
    if (!q) {
      return this.personeller;
    }
    return this.personeller.filter((p) => {
      const ad = this.adGoster(p).toLocaleLowerCase("tr");
      const mail = (p.email || "").toLocaleLowerCase("tr");
      return ad.includes(q) || mail.includes(q);
    });
  }

  mounted() {
    this.load();
  }

  // Roller ve kişiler birlikte yazılır; biri başarısızsa ekran düzenlenemez (boş/bayat seçiciyle
  // rol atanmasın). Yalnız son çağrının sonucu yazılır.
  async load() {
    const no = ++this.yuklemeNo;
    this.yukleniyor = true;
    try {
      const [rolYaniti, kisiYaniti]: any[] = await Promise.all([
        this.$http.get("/api/v1/roles"),
        // DEV-2684: rol KANONİK kaynaktan (model_has_roles) gelsin diye yetki listesi ucundan
        // çekilir; paylaşımlı /personel ucu eski users.role_id ayna kolonuyla ayrışabiliyordu.
        this.$http.get("/api/v1/yetki/kisiler"),
      ]);
      if (no !== this.yuklemeNo) {
        return;
      }
      this.roller = rolYaniti.data || [];
      this.personeller = kisiYaniti.data || [];
      this.yuklemeHatasi = false;
      // Sekme sayaç rozeti için personel sayısını üst bileşene bildir (arama etkilemez).
      this.$emit("sayi", this.personeller.length);
    } catch (hata) {
      if (no === this.yuklemeNo) {
        this.yuklemeHatasi = true;
      }
    } finally {
      if (no === this.yuklemeNo) {
        this.yukleniyor = false;
      }
    }
  }

  // Görünen ad: first_name + last_name (API bu alanları döndürür) -> ad/name -> "-".
  adGoster(item: any): string {
    if (!item) return "-";
    const tamAd = `${item.first_name || ""} ${item.last_name || ""}`.trim();
    return tamAd || item.ad || item.name || "-";
  }

  // Avatar baş harfi (ad yoksa mail'in ilk harfi).
  basHarf(item: any): string {
    const ad = this.adGoster(item);
    const kaynak = ad && ad !== "-" ? ad : (item && item.email) || "?";
    return kaynak.charAt(0).toLocaleUpperCase("tr");
  }

  // Rolün listedeki indeksi (renk paleti indekse göre atanır — matrisle tutarlı).
  rolIndeksi(rolId: number | null): number {
    if (rolId === null) {
      return -1;
    }
    return this.roller.findIndex((r) => r.id === rolId);
  }

  // Rol id'sine göre palet rengi; rol yoksa nötr gri.
  rolRengiById(rolId: number | null): string {
    const i = this.rolIndeksi(rolId);
    return i >= 0 ? rolRengi(i) : NOTR_ROL_RENGI;
  }

  // Avatar rengi: kişinin atanmış rolünün rengi (yoksa nötr).
  avatarRengi(item: any): string {
    return this.rolRengiById(this.seciliRolId(item));
  }

  // Görünen rol KANONİK olarak sunucudan gelir (`gorunen_rol_id`): yönetici admin + meslek rolü
  // taşıyabildiğinden `roles[0]` sıraya bağlı ve yanlış olur. Alan yoksa eski yola düşülür.
  // `roles` bazı uçlarda dizi, bazılarında nesne dönebiliyor (BuroPersonelList kalıbı).
  seciliRolId(item: any): number | null {
    if (item && item.gorunen_rol_id) {
      return item.gorunen_rol_id;
    }
    const roles = item && item.roles;
    if (Array.isArray(roles) && roles.length > 0 && roles[0]) {
      return roles[0].id;
    }
    if (roles && roles.id) {
      return roles.id;
    }
    return item && item.role_id ? item.role_id : null;
  }

  // Satır Sistem Yöneticisi mi? Tek kaynak sunucunun `yonetici` alanı; yoksa rollerden is_admin.
  yoneticiSatiri(item: any): boolean {
    if (!item) {
      return false;
    }
    if (typeof item.yonetici === "boolean") {
      return item.yonetici;
    }
    return Array.isArray(item.roles) && item.roles.some((r: any) => r && r.is_admin == 1);
  }

  // Yönetici rolü yalnız yönetici satırında seçilebilir; diğer satırlarda listeden çıkar.
  rolAdminMi(rol: any): boolean {
    return rol && rol.is_admin !== undefined ? rol.is_admin == 1 : !!rol && rol.id === 1;
  }

  rollerIcin(item: any): any[] {
    return this.yoneticiSatiri(item) ? this.roller : this.roller.filter((r) => !this.rolAdminMi(r));
  }

  // Kişinin tüm rolleri {id, name} olarak (ad yoksa rol listesinden); özel yetki başlığında gösterilir.
  kisiRolleri(item: any): Array<{ id: number; name: string }> {
    const roles = item && Array.isArray(item.roles) ? item.roles : [];
    return roles
        .filter((r: any) => r && r.id)
        .map((r: any) => {
          const kayit = this.roller.find((x) => x.id === r.id);
          return {id: r.id, name: r.name || (kayit ? kayit.name : "")};
        });
  }

  ozelYetkiAc(item: any) {
    this.ozelYetkiKisi = item;
    this.ozelYetkiAcik = true;
  }

  atamaYasak(item: any): boolean {
    // DEV-2684: rol atama (POST users/{user}/roles) Kullanıcı İzinleri Düzenle + ekle ister (/ben
    // anahtarı rol-ekle; yönetici her zaman) ve kişi sunucuya göre kilitli olmamalı. O an atanan satır kilitlenir.
    return this.islemKisitli
        || this.yuklemeHatasi
        || !this.$helper.isAuthorized("rol-ekle")
        || this.kilitNedeni(item) !== null
        || this.atamaSuruyor(item.id);
  }

  atamaSuruyor(kisiId: number): boolean {
    return this.atananlar.includes(kisiId);
  }

  // Sunucunun bu kişi için kilidi (yönetici hesabı, kendisi, sizi aşan kişi): açıklaması ya da null.
  // Kural sunucuda (GET yetki/kisiler duzenlenebilir/duzenleme_engeli); burada kopyalanmaz.
  kilitNedeni(item: any): string | null {
    return duzenlemeEngeli(item);
  }

  // Sizi aşan rol seçilemez (sunucunun `atanabilir` alanı). Yönetici için değişiklik yok.
  rolSecenegiKapali(rol: any): boolean {
    return !this.$helper.isYonetici() && !!rol && rol.atanabilir === false;
  }

  // Kişiye özel yetki diyaloğu: yazma (PUT yetki/kisiler/{user}/modul-yetkileri) Düzenle + değiştir
  // ister (rol-duzenle) ve kişi sunucuya göre kilitli olmamalı; yoksa diyalog salt-okunur açılır
  // (okuma Görüntüle ile serbest). Uç lisanstan muaf olduğu için kısıtlı dönem kapatmaz.
  get ozelYetkiSaltOkunur(): boolean {
    return this.yuklemeHatasi
        || !this.$helper.isAuthorized("rol-duzenle")
        || this.kilitNedeni(this.ozelYetkiKisi) !== null;
  }

  async rolAta(item: any, rolId: number) {
    if (!rolId || rolId === this.seciliRolId(item)) {
      return;
    }
    // Rol değişince kişiye özel yetkiler sıfırlanır: önce onay (istek onaydan sonra gider). Kural personel
    // formuyla ortak (rolDegisimOnayi); sunucu yönetici rolünü korur, yeni küme seçilen roldür.
    const yeniRol = this.roller.find((r) => r.id === rolId) || {id: rolId};
    if (rolDegisimiOnayGerekir(item.ozel_yetki_sayisi, item.roles, [yeniRol])) {
      this.rolOnayBekleyen = {item, rolId};
      this.rolOnayAcik = true;
      return;
    }
    await this.rolAtaGonder(item, rolId);
  }

  get rolOnayMetni(): string {
    return rolDegisimiOnayMetni(this.rolOnayBekleyen ? this.rolOnayBekleyen.item.ozel_yetki_sayisi : 0);
  }

  rolDegisimiVazgec() {
    const bekleyen = this.rolOnayBekleyen;
    this.rolOnayAcik = false;
    this.rolOnayBekleyen = null;
    if (bekleyen) {
      // Seçici eski role dönsün (istek gönderilmedi).
      this.seciciyiYenidenCiz(bekleyen.item.id);
    }
  }

  seciciyiYenidenCiz(kisiId: number) {
    this.$set(this.secimYenileme, kisiId, (this.secimYenileme[kisiId] || 0) + 1);
  }

  async rolDegisimiOnayla() {
    const bekleyen = this.rolOnayBekleyen;
    this.rolOnayAcik = false;
    this.rolOnayBekleyen = null;
    if (bekleyen) {
      await this.rolAtaGonder(bekleyen.item, bekleyen.rolId);
    }
  }

  async rolAtaGonder(item: any, rolId: number) {
    // Seçici kapalı olsa da işlev yeniden girişe kapalı; ikinci seçim gönderilmez, seçici sunucu rolüne döner.
    if (this.atamaSuruyor(item.id)) {
      this.seciciyiYenidenCiz(item.id);
      return;
    }
    this.atananlar.push(item.id);
    try {
      // UserRoleController@store: syncRoles (tam değişim). Tek rol atanır.
      await this.$http.post(`/api/v1/users/${item.id}/roles`, {roles: [rolId]});
      this.$toast.success("Rol atandı.");
    } catch (error) {
      // Hata (403/409/422) global kesicide gösterilir. Görünen rol değişmeyeceğinden seçici
      // kendi iç değerinde kalırdı; sunucudaki role dönsün diye yeniden çizilir.
      this.seciciyiYenidenCiz(item.id);
    } finally {
      this.atananlar = this.atananlar.filter((id) => id !== item.id);
      // Gerçek durumu backend'den tazele (başarısızlıkta eski değer geri gelir).
      this.load();
    }
  }
}
